import * as Sentry from "@sentry/nextjs"
import { Button, Spinner, Tooltip } from "flowbite-react"
import moment from "moment"
import { useRouter } from "next/router"
import React, { Dispatch, SetStateAction, useEffect, useState, useMemo, useCallback } from "react"
import useSWR, { useSWRConfig } from "swr"

import { PhoneTelephoneIcon, TimeIcon, TwoPeopleIcon } from "@assets/icons"
import CustomImage from "@components/elements/CustomImage"
import DangerouslyHTMLTextPriview from "@components/elements/DangerouslyHTMLTextPriview"
import MonoImageGallery from "@components/elements/MonoImageGallery/MonoImageGallery"
import MonoSelect from "@components/elements/Select/MonoSelect"
import useToast from "@hooks/useToast"
import InfoIcon from "@icons/Info"
import LifePreServer from "@icons/LifePreServer"
import LocationBuildings from "@icons/LocationBuildings"
import LocationMark from "@icons/LocationMark"
import RightArrow from "@icons/RightArrow"
import { ProductInfoFormAttributes } from "@modules/tenants/OrdersPage/Components/AddOrderModal/ProductInfoForm/ProductInfoForm"
import WidgetsDatePicker from "@modules/widgets/components/WidgetsDatePicker"
import { getSplittedDateRanges } from "@mono/essentials/entities/calendar/helpers"
import {
  EventAvailablilityADayType,
  GetAllEventsResponseType,
} from "@mono/essentials/entities/calendar/types"
import { usePublicGetAvailabilityForMonth } from "@mono/essentials/hooks/WidgetHooks"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import * as ProductPricingServices from "@mono/essentials/services/ProductPricingServices"
import * as ProductServices from "@mono/essentials/services/ProductServices"
import { CreateProductCategoryInfoRequestType } from "@mono/essentials/types/ProductTypes"
import { GetProductLocationFormAttributes } from "@mono/essentials/types/ProductTypes"
import { IDomain } from "@mono/essentials/types/UserTypes"
import { getDateInDomainTimezone, toLocalDate } from "@mono/essentials/utils/Common"
import { closureType } from "@mono/essentials/utils/Constant"

import { createDropdownOptionsForUnit } from "../../../../utils/common"

import { ProductLocation } from "./ProductionLocation"

type availabilityType = {
  start_time: string
}
interface ProductDataType extends CreateProductCategoryInfoRequestType {
  availability: availabilityType
  start_time: string
}

const NoSlotsAvailable = () => {
  return (
    <div className={"text12Semibold text-neutral-500 text-center mt-2 py-2"}>
      No slots available
    </div>
  )
}
export interface PreviewType {
  pagePreview?: string
  selectedProduct: string | undefined
  setProductInfo?: Dispatch<React.SetStateAction<ProductInfoFormAttributes | undefined>>
  goToNextStep?: (category_id?: string, product_id?: string, product_info?: any) => void
  setOrderSummery?: Dispatch<React.SetStateAction<any | undefined>>
  setShow?: Dispatch<SetStateAction<boolean>>
  isIntegratedPage: boolean
  domainDetails: IDomain
  isSelectedDate?: Date | null
  isLoading?: boolean
  formInitialValues?: any
  isModal?: any
  isFullPage?: boolean
  selectedProductData?: any
  setIsFromPagePreview?: Dispatch<React.SetStateAction<boolean>>
  onSubmit?: (arg0: ProductInfoFormAttributes) => Promise<void>
  hideQuantityEdit?: boolean
}

interface ISlotsTypes {
  label: string
  value: {
    start_time: string
    available: number
    slot_id: string
    end_time: string
    is_past_time: boolean
    reopenable_for: string | null
    reopenable_reason: string | null
  }
}

export interface ProductPriceTypeData {
  quantity: number
  minimum_participants_required_for_slot: number
  perCustomer?: boolean
  isMandatory?: boolean
  unitCount: number
  unitName: string | undefined
  unitId: string | undefined
  unitDescription: string
  price: string
  is_minimum_fullfilled?: boolean
  productId: string
  unitPrice: number
  formatted_unit_price?: string
  formatted_price?: string
}

let isMinimumFullfilledForAll: any
let totalGuests: any
let tooltipParticipantData: any
let sumOfUnitCount: any = 0

const ProductDetailsPreview = ({
  pagePreview,
  selectedProduct = "",
  setProductInfo,
  goToNextStep,
  setOrderSummery,
  isIntegratedPage,
  setShow,
  isSelectedDate,
  isLoading,
  domainDetails,
  formInitialValues,
  isModal,
  isFullPage,
  selectedProductData,
  setIsFromPagePreview,
  onSubmit,
  hideQuantityEdit = false,
}: PreviewType) => {
  const { cache } = useSWRConfig()
  const [initialValues, setInitialValues] = useState(formInitialValues)
  const router = useRouter()

  const { datepicker, nextStep } = router.query

  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    initialValues?.date
      ? initialValues?.date
      : (getDateInDomainTimezone(
          isSelectedDate || (selectedProductData?.first_available_date ?? ""),
          "Y-m-d"
        ) as string)
  )
  const [availabilityMonth, setAvailabilityMonth] = useState<string>(
    initialValues?.date
      ? initialValues?.date
      : (getDateInDomainTimezone(
          isSelectedDate || (selectedProductData?.first_available_date ?? ""),
          "Y-m-d"
        ) as string)
  )
  const [selectedPriceTypeData, setSelectedPriceTypeData] = useState<ProductPriceTypeData[]>([])
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<any>(
    formInitialValues?.time ?? undefined
  )
  const [capacityLoading, setCapacityLoading] = useState<boolean>(false)
  const [liveCapacityError, setLiveCapacityError] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { showToast } = useToast()
  const selectedProductDetail = selectedProductData ?? {}

  const { data: locationData = [] as GetProductLocationFormAttributes[], error: hasTypeError } =
    useSWR(
      router.isReady && selectedProduct && pagePreview === "with_all_details"
        ? `booking_location_${selectedProduct}-${isModal}`
        : null,
      async () => {
        try {
          const { data } = await ProductServices.getAttachedLocationOfProduct(
            selectedProduct,
            isIntegratedPage
          )
          return data
        } catch (e) {
          Sentry.captureException(e)
          console.error(e.response.data.message)
          showToast({ heading: e.response.data.message, type: "danger" })
        }
      },
      {
        revalidateOnFocus: false,
      }
    )

  const images = selectedProductDetail?.mediaFiles?.map((image: any) => {
    return {
      original: `${image.public_url}`,
      thumbnail: `${image.public_url}`,
    }
  })

  const isDefaultView = (pagePreview && pagePreview === "default_view") || false

  const {
    data: productAvailabilityPerMonthData,
    isLoading: isProductAvailabilityPerMonthDataLoading,
  } = usePublicGetAvailabilityForMonth({
    isIntegratedPage,
    dateRanges:
      datepicker === "dropdown" || datepicker === "list"
        ? [
            {
              from_date: moment(getDateInDomainTimezone("", "Y-m-d")).format("YYYY-MM-DD"),
              to_date: moment(
                getDateInDomainTimezone(selectedProductData?.last_available_end_date ?? "", "Y-m-d")
              )
                .endOf("year")
                .format("YYYY-MM-DD"),
            },
          ]
        : getSplittedDateRanges({
            from_date: moment(availabilityMonth).startOf("month").format("YYYY-MM-DD"),
            to_date: moment(availabilityMonth).endOf("month").format("YYYY-MM-DD"),
          }),
    queryOptions: {
      enabled: Boolean(selectedProduct && availabilityMonth),
    },
    filters: {
      products: [selectedProductDetail?.id],
    },
  })

  const {
    data: productAvailabilityPerDateData = {},
    isValidating: isProductAvailabilityPerDateDataValidating,
  } = useSWR(
    selectedProduct && selectedDate
      ? `productAvailabilityPerDateData-${selectedProduct}-${selectedDate}-${isModal}`
      : null,
    async () => {
      const date = getDateInDomainTimezone(selectedDate, "Y-m-d") as string
      try {
        const { data: getProductAvailabilityForDateResponse } =
          await OrderServices.getProductAvailabilityForDate({
            productId: selectedProduct ?? "",
            date: selectedDate ?? "",
            isIntegratedPage,
          })
        if (getProductAvailabilityForDateResponse) {
          return getProductAvailabilityForDateResponse
        }
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      }
    },
    {
      revalidateOnFocus: false,
      refreshWhenHidden: false,
      shouldRetryOnError: false,
    }
  )

  const { data: productAvailabilityData } = useSWR(
    selectedProductDetail?.id
      ? `productAvailabilityData-${selectedProductDetail?.id}-${isModal}`
      : null,
    async () => {
      try {
        const { data: getProductAvailabilityResponse } = await OrderServices.getProductAvailability(
          selectedProductDetail?.id,
          isIntegratedPage
        )
        if (getProductAvailabilityResponse.data) {
          return getProductAvailabilityResponse.data
        }
        return []
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      }
    },
    {
      keepPreviousData: false,
      revalidateOnFocus: false,
      refreshWhenHidden: false,
      shouldRetryOnError: false,
    }
  )

  const {
    data: productPriceData,
    mutate: refetchProductPriceData,
    isValidating: isProductPricingPerMonthDataisValidating,
  } = useSWR(
    (selectedTimeSlot && selectedTimeSlot?.slot_id) || initialValues?.time
      ? `productPriceData-${selectedTimeSlot}-${selectedTimeSlot?.slot_id}-${isModal}`
      : null,
    async () => {
      try {
        const { data: getProductPriceDataResponse } =
          await ProductPricingServices.getProductPricingByProdId(
            selectedProduct ?? "",
            {},
            isIntegratedPage,
            selectedTimeSlot?.slot_id || initialValues?.time
          )
        if (getProductPriceDataResponse) {
          return getProductPriceDataResponse.data
        }
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      }
    },
    {
      revalidateOnFocus: false,
    }
  )

  useEffect(() => {
    if (productPriceData?.length) {
      const _price_data = productPriceData?.map((productPrice, index) => ({
        unitName: productPrice.unit?.name,
        unitId: productPrice.unit?.id,
        unitCount:
          initialValues?.units?.length > 0
            ? initialValues?.units?.find((unit) => unit.unitId === productPrice?.unit?.id)
                ?.unitCount
            : selectedTimeSlot?.available === 1 && productPriceData?.length === 1
              ? index == 0
                ? 1
                : 0
              : 0,
        unitDescription: productPrice.unit?.description || "",
        price: productPrice.formatted_price,
        min_quantity: productPrice?.unit?.min_quantity,
      }))
      fetchCapacityData([..._price_data])
      setSelectedPriceTypeData(_price_data)
    }
  }, [productPriceData])

  const handleNextStep = async () => {
    const unitsDataofSelectedProduct = selectedPriceTypeData
      ?.filter((item) => item.unitCount > 0)
      ?.map((item) => {
        return {
          unitName: item.unitName,
          unitId: item.unitId,
          unitCount: item.unitCount,
          price: item.price,
        }
      })

    const selectedProductInfo: any = {
      date: getDateInDomainTimezone(selectedDate, "Y-m-d"),
      time: selectedTimeSlot?.slot_id || initialValues?.time,
      units: unitsDataofSelectedProduct,
    }

    setOrderSummery?.({
      selectedProductDetail,
      selectedPriceTypeData: selectedPriceTypeData.filter((item) => item.unitCount > 0),
      productAvailabilityData,
      selectedTimeSlot,
      selectedDate,
      selectedProductDurationDetails: {
        date: getDateInDomainTimezone(selectedDate, "Y-m-d"),
        time: selectedTimeSlot?.slot_id || initialValues?.time,
      },
    })
    setIsSubmitting(true)
    await onSubmit?.({
      date: getDateInDomainTimezone(selectedDate, "Y-m-d"),
      time: selectedTimeSlot?.slot_id || initialValues?.time,
      units: selectedPriceTypeData?.map((item) => {
        return {
          unitName: item.unitName,
          unitId: item.unitId,
          unitCount: item.unitCount,
          price: item.price,
        }
      }),
    })
    setProductInfo?.({
      date: getDateInDomainTimezone(selectedDate, "Y-m-d"),
      time: selectedTimeSlot?.slot_id || initialValues?.time,
      units: selectedPriceTypeData?.map((item) => {
        return {
          unitName: item.unitName,
          unitId: item.unitId,
          unitCount: item.unitCount,
          price: item.price,
        }
      }),
    })
    goToNextStep?.("", selectedProductDetail?.id, selectedProductInfo)
    setIsFromPagePreview?.(true)
    setShow?.(true)
    setIsSubmitting(false)
  }

  useEffect(() => {
    setOrderSummery?.({
      selectedProductDetail,
      productAvailabilityData,
      selectedPriceTypeData,
    })
  }, [selectedProductDetail, productAvailabilityData, selectedPriceTypeData])

  const getCalendarDates = useMemo(() => {
    const availableDates: Date[] = []
    const unavailableDates: Date[] = []

    productAvailabilityPerMonthData?.forEach((event: GetAllEventsResponseType) => {
      event.availability.forEach((day: EventAvailablilityADayType) => {
        const date = moment(day.slot_local_date)
        if (date.isValid()) {
          if (day.available > 0) {
            availableDates.push(toLocalDate(date))
          } else {
            unavailableDates.push(toLocalDate(date))
          }
        }
      })
    })

    return {
      availableDates,
      unavailableDates,
    }
  }, [productAvailabilityPerMonthData])

  const datePickerValue = useMemo(() => {
    return (Array.isArray(datepicker) ? datepicker[0] : datepicker) || ""
  }, [datepicker])

  const handleCalendar = () => {
    return (
      <WidgetsDatePicker
        inline
        isSelectedDate={toLocalDate(moment(selectedDate))}
        popperClassName={
          "lg:px-4 md:px-2 px-4 pt-4 pb-3 !border !border-neutral-200 !inline-grid md:w-full sm:mx-0 shadow bg-white inline-date-picker-grid"
        }
        containerClassName={`md:inline !w-auto flex justify-center`}
        loading={isProductAvailabilityPerMonthDataLoading}
        showSpinner={isProductAvailabilityPerMonthDataLoading}
        onMonthChange={(props: Date | null) => {
          const date = getDateInDomainTimezone(props ?? "", "Y-m-d") as string
          setAvailabilityMonth(date)
          setSelectedDate(date)
          setSelectedTimeSlot(null)
          setInitialValues((prev: ProductInfoFormAttributes) =>
            hideQuantityEdit ? { units: prev?.units } : undefined
          )
        }}
        excludeDates={getCalendarDates.unavailableDates}
        minDate={toLocalDate()}
        onChange={(date: Date | null) => {
          setSelectedDate(getDateInDomainTimezone(date ?? "", "Y-m-d") as string)
          setSelectedTimeSlot(null)
          setInitialValues((prev: ProductInfoFormAttributes) =>
            hideQuantityEdit ? { units: prev?.units } : undefined
          )
        }}
        highlightDates={getCalendarDates.availableDates}
        domainDetails={domainDetails}
        isDefaultViewWidget={isDefaultView}
        datepicker={
          datepicker && !nextStep
            ? "list"
            : nextStep === "true" && window !== window.parent
              ? datePickerValue
              : ""
        }
      >
        <div className={"mt-2 flex items-center justify-start gap-2"}>
          <span className={"text13Medium flex items-center text-green-600"}>
            <span className={"mr-1 h-1 w-1 rounded-full bg-green-600"} />
            Available
          </span>
          <span className={"text13Medium flex items-center text-neutral-500"}>
            <span className={"mr-1 h-1 w-1 rounded-full bg-neutral-500"} />
            Unavailable
          </span>
          <span className={"text13Medium flex items-center text-primary-600"}>
            <span className={"mr-1 h-1 w-1 rounded-full bg-primary-600 "} />
            Selected
          </span>
          <span className={"text13Medium flex items-center text-orange-450"}>
            <span className={"mr-1 h-1 w-1 rounded-full bg-orange-450 "} />
            Today
          </span>
        </div>
      </WidgetsDatePicker>
    )
  }

  const handleCounter = useCallback(() => {
    sumOfUnitCount = selectedPriceTypeData.reduce((sum, obj) => sum + obj.unitCount, 0)
    isMinimumFullfilledForAll = selectedPriceTypeData.every((item) => item?.is_minimum_fullfilled)
    totalGuests = selectedPriceTypeData?.[0]?.minimum_participants_required_for_slot
    tooltipParticipantData = selectedPriceTypeData
      .map((item) => `${item.quantity} ${item.unitName}`)
      .join(" & ")

    return (
      <>
        {selectedPriceTypeData?.length > 0 && (
          <>
            <div
              className={`${isDefaultView ? "lg:grid-cols-2 grid lg:gap-x-4 lg:gap-y-3 gap-y-2 gap-x-2 min-[994px]:grid-cols-2" : ""}`}
            >
              {(selectedPriceTypeData || [])?.map((product: any, index: number) => (
                <div
                  className={`border border-neutral-450  rounded-md p-3 mb-4 shadow bg-white`}
                  data-cy={"ticket-variant"}
                  key={`${index}-price-box`}
                >
                  <div className={"items-center gap-1 mb-2.5"}>
                    <div className={"flex items-center mb-1"}>
                      <span className={"text-neutral-1000 text15Medium text-wrap"}>
                        {`${product?.unitName}`}
                      </span>
                    </div>
                    {(product?.min_quantity || product?.unitDescription) && (
                      <div className={"flex items-center"}>
                        <span className={"text-neutral-600 text13Medium text-wrap"}>
                          <span
                            className={"border-r border-neutral-500 mr-1 pr-1"}
                          >{`${product?.formatted_unit_price ?? ""} Per ${product?.unitName}`}</span>
                          <span>{`(${product && product.min_quantity ? `Min* required - ${product.min_quantity}` : ""}${product && product.min_quantity && product.unitDescription ? "," : ""}${product ? product.unitDescription : ""})`}</span>
                        </span>
                      </div>
                    )}
                    {selectedProductDetail?.is_unit_assets_linked ? (
                      <p className={"paragraph13 font-medium text-green-500"}>
                        {capacityLoading ? (
                          <Spinner color={"success"} size={"xs"} />
                        ) : (
                          <>({product?.total_live_capacity} Available)</>
                        )}
                      </p>
                    ) : null}
                  </div>
                  <div className={"flex items-center justify-between"}>
                    <div className={"items-center"}>
                      {domainDetails?.quantity_indicator === "increment_decrement_button" ? (
                        <div className={"flex items-center gap-3"}>
                          <div>
                            <button
                              type={"button"}
                              tabIndex={-1}
                              className={`${!initialValues?.time && !selectedTimeSlot && "cursor-not-allowed"}`}
                              onClick={() => {
                                handleProductPrice(-1, index, false)
                              }}
                              disabled={
                                product.unitCount === 0 ||
                                (!initialValues?.time && !selectedTimeSlot)
                              }
                            >
                              <span
                                className={`${
                                  product.unitCount === 0 ||
                                  (!initialValues?.time && !selectedTimeSlot)
                                    ? "text-primary-400 cursor-not-allowed bg-primary-200"
                                    : "text-primary-600 bg-primary-300"
                                } m-auto block h-8 w-8 rounded-md text-xl`}
                              >
                                −
                              </span>
                            </button>
                          </div>
                          <div
                            className={
                              "flex h-8 w-12 items-center text-neutral-1000 justify-center px-1 py-1 border border-neutral-450 rounded-md"
                            }
                          >
                            <div className={"text15Medium"}>{product.unitCount}</div>
                          </div>
                          <div>
                            <button
                              type={"button"}
                              className={`${!initialValues?.time && !selectedTimeSlot && "cursor-not-allowed"}`}
                              tabIndex={-1}
                              onClick={() => {
                                handleProductPrice(1, index, false)
                              }}
                              disabled={
                                product.unitCount >= product?.total_live_capacity ||
                                sumOfUnitCount === selectedTimeSlot?.available ||
                                capacityLoading
                              }
                            >
                              <span
                                className={`${
                                  product.unitCount >= product?.total_live_capacity ||
                                  sumOfUnitCount === selectedTimeSlot?.available ||
                                  capacityLoading
                                    ? "text-primary-400 cursor-not-allowed bg-primary-200"
                                    : "text-primary-600 bg-primary-300"
                                } m-auto block h-8 w-8 rounded-md text-xl`}
                              >
                                +
                              </span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {product?.total_live_capacity === 0 ? (
                            <Tooltip content={"No availability"} placement={"top"} style={"dark"}>
                              <div>
                                <MonoSelect
                                  value={{
                                    label: product?.unitCount,
                                    value: product?.unitCount,
                                  }}
                                  isDisabled
                                  options={[]}
                                  onValueChange={(newValue) => {
                                    handleProductPrice(newValue?.value, index, true)
                                  }}
                                  menuPosition={"fixed"}
                                  className={""}
                                  name={"select_unit_count"}
                                  isSearchable={false}
                                  isFromWidget
                                  isLoading={false}
                                />
                              </div>
                            </Tooltip>
                          ) : (
                            <MonoSelect
                              value={{
                                label: product?.unitCount,
                                value: product?.unitCount,
                              }}
                              isDisabled={capacityLoading}
                              options={createDropdownOptionsForUnit(
                                product?.total_live_capacity || 0
                              )}
                              onValueChange={(newValue) => {
                                handleProductPrice(newValue?.value, index, true)
                              }}
                              menuPlacement={"auto"}
                              menuPosition={"fixed"}
                              className={""}
                              name={"select_unit_count"}
                              isSearchable={false}
                              isFromWidget
                              isLoading={productPriceData?.length === 0 || capacityLoading}
                            />
                          )}
                        </>
                      )}
                    </div>
                    {product?.formatted_unit_price && (
                      <div className={"rounded-md bg-neutral-250 px-2 py-1"}>
                        <span
                          className={"text14Semibold text-neutral-800"}
                        >{`${product?.formatted_unit_price ?? ""}`}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </>
    )
  }, [selectedPriceTypeData, selectedTimeSlot, capacityLoading])

  const formattedAndValidatedTimeSlots = useMemo(() => {
    const timeSlots: ISlotsTypes[] = []
    if (productAvailabilityPerDateData) {
      Object.keys(productAvailabilityPerDateData)
        // .filter(date => productAvailabilityPerDateData && productAvailabilityPerDateData[date].available)
        .sort(
          (objA, objB) =>
            Number(new Date(productAvailabilityPerDateData[objA].local_start_time ?? "")) -
            Number(new Date(productAvailabilityPerDateData[objB].local_start_time ?? ""))
        )
        .map((date) => {
          const currentDateTime = moment()
          if (
            productAvailabilityPerDateData[date].available ||
            productAvailabilityPerDateData[date].reopenable_for ===
              closureType.CLOSED_EXTERNAL_CALL_TO_BOOK_AND_OPEN_INTERNAL
          ) {
            const local_start_time = productAvailabilityPerDateData
              ? productAvailabilityPerDateData[date].local_start_time
              : ""
            const local_end_time = productAvailabilityPerDateData
              ? productAvailabilityPerDateData[date].local_end_time
              : ""
            const startTime = moment(local_start_time)
            const endTime = moment(local_end_time).format("hh:mm A")
            const isSlotIdGreaterThanCurrentDateTime =
              moment(local_start_time).isAfter(currentDateTime)

            // if (isSlotIdGreaterThanCurrentDateTime) {
            timeSlots.push({
              label:
                productAvailabilityPerDateData &&
                productAvailabilityPerDateData[date].reopenable_for ===
                  closureType.CLOSED_EXTERNAL_CALL_TO_BOOK_AND_OPEN_INTERNAL
                  ? `${startTime.format("hh:mm A")} - Select For availibilities`
                  : `${startTime.format("hh:mm A")} ${!selectedProductDetail?.is_unit_assets_linked ? `- ${productAvailabilityPerDateData && productAvailabilityPerDateData[date].available} Available` : ""}`,
              value: {
                start_time: startTime.format("hh:mm A"),
                available:
                  productAvailabilityPerDateData && productAvailabilityPerDateData[date].available,
                slot_id:
                  productAvailabilityPerDateData && productAvailabilityPerDateData[date].slot_id,
                end_time: endTime,
                is_past_time: !isSlotIdGreaterThanCurrentDateTime,
                reopenable_for:
                  productAvailabilityPerDateData &&
                  productAvailabilityPerDateData[date].reopenable_for,
                reopenable_reason:
                  productAvailabilityPerDateData &&
                  productAvailabilityPerDateData[date].reopenable_reason,
              },
            })
            // }
          }
        })
    }

    return timeSlots
  }, [productAvailabilityPerDateData, selectedProductDetail])

  // Auto-select first slot when only one is available
  useEffect(() => {
    if (formattedAndValidatedTimeSlots.length === 1 && !selectedTimeSlot) {
      setSelectedTimeSlot(formattedAndValidatedTimeSlots[0]?.value)
    }
  }, [formattedAndValidatedTimeSlots, selectedTimeSlot])

  const [isLoadingLiveCapacity, setIsLoadingLiveCapacity] = useState<{ [key: string]: boolean }>({})
  const fetchCapacityData = async (allPriceData, index?: number) => {
    try {
      setCapacityLoading(true)
      if (index) {
        setIsLoadingLiveCapacity({ ...isLoadingLiveCapacity, [index]: true })
      }
      if (selectedTimeSlot?.available > 0 || initialValues?.time) {
        setLiveCapacityError("")
        const _allData = allPriceData?.map((price) => {
          return {
            unit_id: price.unitId,
            quantity: price.unitCount ? price.unitCount : 0,
          }
        })

        const payload = {
          quantities: _allData,
          slot_id: selectedTimeSlot?.slot_id || initialValues?.time,
        }

        const { data } = await OrderServices.getLiveCapacityByProductId(
          selectedProductDetail?.id,
          payload,
          isIntegratedPage
        )
        const capacityData = [...data]

        const mergedArray = capacityData.map((item1) => {
          const matchingItem = allPriceData.find((item2) => item2.unitId === item1.unit_id)
          return {
            ...matchingItem,
            ...item1,
          }
        })

        setSelectedPriceTypeData(mergedArray)
      }
    } catch (e) {
      Sentry.captureException(e)
      console.error(e)
      setLiveCapacityError(e?.response?.data?.message ?? "something went wrong!")
    } finally {
      setCapacityLoading(false)
      if (index) {
        setIsLoadingLiveCapacity({ ...isLoadingLiveCapacity, [index]: false })
      }
    }
  }

  const handleProductPrice = async (value: number, id: number, isSelect: boolean) => {
    try {
      const _oldData = selectedPriceTypeData
      for (const index in _oldData) {
        if (Number(index) === id) {
          _oldData[index].unitCount = isSelect ? value : _oldData[index].unitCount + value
          break
        }
      }

      setSelectedPriceTypeData([..._oldData])
      fetchCapacityData([..._oldData], id)
    } catch (e) {
      Sentry.captureException(e)
      console.error({ e })
    }
  }

  const hasNoParticipants = selectedPriceTypeData.filter((item) => item.unitCount > 0).length === 0

  const isMinimumFullfilledForAllParticipants = selectedPriceTypeData.every(
    (item) => item?.is_minimum_fullfilled
  )

  const handleScrollInToViewFunction = () => {
    const element = document.getElementById("booking-container")
    element.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" })
  }

  const isLiveCapacityApiLoading = useMemo(() => {
    return Object.values(isLoadingLiveCapacity).some((value) => value)
  }, [isLoadingLiveCapacity])

  const timeSelectionValue = useMemo(() => {
    return formattedAndValidatedTimeSlots.find(
      (option) => option?.value?.slot_id === (initialValues?.time || selectedTimeSlot?.slot_id)
    )
  }, [initialValues?.time, selectedTimeSlot, formattedAndValidatedTimeSlots])

  return (
    <>
      {isDefaultView ? (
        <div className={"sm:p-4"}>
          <div className={"sm:flex gap-5"}>
            <div>{handleCalendar()}</div>
            <div className={"border border-solid sm:block hidden"} />
            <div className={"w-full"}>
              <div className={""}>
                {isProductAvailabilityPerDateDataValidating ? (
                  <div className={"text-center mt-2"}>
                    <Spinner size={"md"} light className={"text-primary-400 fill-white"} />
                  </div>
                ) : (
                  <div className={"sm:mb-4 mb-2 sm:mt-0 mt-4"}>
                    {formattedAndValidatedTimeSlots.length > 0 ? (
                      <div>
                        <MonoSelect
                          placeholder={"Select Time"}
                          value={timeSelectionValue}
                          options={formattedAndValidatedTimeSlots}
                          isSearchable={false}
                          onValueChange={(newValue) => {
                            setSelectedTimeSlot(newValue?.value)
                          }}
                          selectedValueColor={"text-neutral-1000"}
                          menuPosition={"fixed"}
                          className={"bg-whiteBlack-100 !rounded-md"}
                          name={"select_time"}
                          prefixIcon={
                            <TimeIcon className={"text-neutral-600 text16Regular w-4 h-4 mt-0.5"} />
                          }
                          isFromWidget
                        />
                      </div>
                    ) : (
                      <NoSlotsAvailable />
                    )}
                  </div>
                )}
                {isProductPricingPerMonthDataisValidating ? (
                  <>
                    <div className={"text-center mt-2"}>
                      <Spinner size={"md"} light className={"text-primary-400 fill-white"} />
                    </div>
                  </>
                ) : (
                  <>
                    {!isProductAvailabilityPerDateDataValidating &&
                      (selectedTimeSlot?.available > 0 || initialValues?.time) && (
                        <div className={""}>{handleCounter()}</div>
                      )}
                  </>
                )}
              </div>
              {selectedTimeSlot?.available === 0 && selectedTimeSlot?.reopenable_reason ? (
                <div className={"mt-6 sm:mx-0 mx-4"}>
                  <div
                    className={`bg-orange-200 border-yellow-400 border-t border-b p-3 paragraph16Regular`}
                  >
                    <div className={"flex items-center gap-2"}>
                      <div>
                        <PhoneTelephoneIcon className={"text-yellow-600 mt-1"} />
                      </div>
                      <div className={"paragraph14Medium"}>
                        {selectedTimeSlot?.reopenable_reason}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              <div>
                {selectedTimeSlot &&
                !isProductAvailabilityPerDateDataValidating &&
                selectedProductDetail?.minimum_participants_booking_display_message ? (
                  <div
                    className={`${
                      !isMinimumFullfilledForAll
                        ? "bg-red-200 border-red-500"
                        : "bg-green-200 border-green-800"
                    } border-t border-b p-3 paragraph16Regular mt-2`}
                  >
                    <div className={"flex items-center gap-2"}>
                      <div>
                        <TwoPeopleIcon className={"w-4 h-4"} />
                      </div>
                      <div className={"paragraph14Medium"}>
                        {selectedProductDetail?.minimum_participants_booking_display_message}
                      </div>
                    </div>
                    <div className={"flex items-center gap-1 mt-1"}>
                      <span
                        className={` ${
                          !isMinimumFullfilledForAll ? "bg-red-600" : "bg-green-800"
                        } w-2 h-2 border rounded-full ml-1 mr-2`}
                      />
                      <div
                        className={`text14Medium ${
                          !isMinimumFullfilledForAll ? "text-red-600" : "text-green-800"
                        }`}
                      >{`${sumOfUnitCount} Guests`}</div>
                      <Tooltip
                        content={`There is total ${
                          totalGuests ?? totalGuests
                        } guest required. Minimum ${tooltipParticipantData} must required. Maximum you can select any number`}
                        trigger={"hover"}
                        className={"text-xs w-64 text-center"}
                      >
                        <InfoIcon className={"w-3.5 h-3.5"} />
                      </Tooltip>
                    </div>
                  </div>
                ) : null}
                {liveCapacityError ? (
                  <p className={"text12Regular mt-4 text-red-600"}>
                    {liveCapacityError ?? "Something went wrong! Please try again later"}
                  </p>
                ) : null}
                <div className={"pt-4"}>
                  <Button
                    color={"primary"}
                    className={"w-full [&>span]:!text16Medium [&>span]:!py-[13px] !h-10"}
                    onClick={handleNextStep}
                    size={"sm"}
                    disabled={
                      (initialValues?.time ? !initialValues?.time : !selectedTimeSlot) ||
                      hasNoParticipants ||
                      !isMinimumFullfilledForAllParticipants ||
                      capacityLoading ||
                      isSubmitting ||
                      Boolean(liveCapacityError) ||
                      isLiveCapacityApiLoading
                    }
                  >
                    {isLoading || capacityLoading || isSubmitting || isLiveCapacityApiLoading ? (
                      <Spinner size={"sm"} light className={"text-primary-400 fill-white"} />
                    ) : (
                      <>
                        Next
                        <RightArrow
                          className={"ml-2.5 text-whiteBlack-100"}
                          width={20}
                          height={20}
                        />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`md:grid md:grid-cols-2 xl:grid-cols-[720px_396px] md:h-full lg:grid-cols-5 ${isModal ? "" : "justify-center px-4 lg:px-8"}`}
        >
          {" "}
          <div
            className={`md:border-neutral-200 md:border-r xl:col-span-1 lg:col-span-3 md:overflow-y-auto pb-5 ${isModal ? "px-0 xl:pr-8 sm:pr-6" : "px-0 xl:pr-8 sm:pr-6"}`}
          >
            <div className={"flex"}>
              <div className={"w-full"}>
                {pagePreview === "with_all_details" ? (
                  <>
                    {selectedProductDetail &&
                    selectedProductDetail?.mediaFiles &&
                    selectedProductDetail?.mediaFiles?.length > 0 ? (
                      <MonoImageGallery images={images || []} ContainerClassName={"w-full"} />
                    ) : (
                      <div
                        className={
                          "relative flex h-full min-h-[300px] max-[425px]:min-h-[220px] w-full cursor-pointer flex-row items-end rounded-lg bg-imagePlaceholderBackground bg-cover bg-center shadow-sm "
                        }
                      >
                        <div
                          className={
                            "absolute left-1/2 top-1/2 h-11 w-[52px] -translate-x-1/2 -translate-y-1/2 transform bg-transparent bg-imagePlaceholder bg-contain bg-center bg-no-repeat"
                          }
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {selectedProductDetail &&
                    selectedProductDetail?.mediaFiles &&
                    selectedProductDetail?.mediaFiles?.length > 0 ? (
                      <>
                        {images && images.length > 0 && (
                          <>
                            <CustomImage
                              src={images[0]?.original}
                              className={"overflow-hidden w-full h-80 rounded-lg"}
                              fill={false}
                              height={window.innerWidth <= 900 ? 220 : 360}
                              width={590}
                            />
                            <div className={`mt-3 grid grid-cols-2 gap-3`}>
                              {(images?.slice(1) || []).map((image: any) => {
                                return (
                                  <div>
                                    <CustomImage
                                      src={image.original}
                                      className={"object-cover h-44 w-full rounded-lg"}
                                      fill={false}
                                      height={170}
                                      width={287}
                                    />
                                  </div>
                                )
                              })}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div
                        className={
                          "relative flex h-full min-h-[300px] max-[425px]:min-h-[220px] w-full cursor-pointer flex-row items-end rounded-lg bg-imagePlaceholderBackground bg-cover bg-center shadow-sm "
                        }
                      >
                        <div
                          className={
                            "absolute left-1/2 top-1/2 h-11 w-[52px] -translate-x-1/2 -translate-y-1/2 transform bg-transparent bg-imagePlaceholder bg-contain bg-center bg-no-repeat"
                          }
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            {pagePreview === "with_all_details" && (
              <Button
                color={"primary"}
                className={"w-full mt-4 md:hidden !h-10"}
                onClick={handleScrollInToViewFunction}
              >
                Book Now
              </Button>
            )}
            {pagePreview === "with_all_details" && (
              <div>
                {locationData?.length > 0 ? (
                  <div className={"mt-2"}>
                    <div className={"w-full border-b border-neutral-200"} />
                    {locationData?.map((location, index) => {
                      return (
                        <div className={"my-3 flex"} key={index}>
                          {location.type === "office" ? (
                            <div className={"mb-4 sm:mb-0"}>
                              <LocationBuildings className={"mr-1 text-primary-600"} />
                            </div>
                          ) : (
                            <div className={"mb-8 sm:mb-0"}>
                              <LocationMark className={"mr-1 text-primary-600"} />
                            </div>
                          )}
                          <div className={"text14Medium text-primary-600"}>
                            <ProductLocation location={location} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : null}
                <div className={"w-full border-b border-neutral-200 my-3"} />
                <div className={"mt-4 mb-3"}>
                  <DangerouslyHTMLTextPriview
                    content={selectedProductDetail?.description?.toString() ?? ""}
                    className={"paragraph14Regular"}
                  />
                </div>
                {selectedProductDetail?.cancellation_policy ? (
                  <div
                    className={"flex items-center justify-start rounded-lg bg-[#F9EDDC] p-4 mt-4"}
                  >
                    <div className={"flex flex-col gap-1"}>
                      <span className={"text15Semibold text-neutral-1000 flex items-center gap-1"}>
                        <span>
                          <LifePreServer />
                        </span>
                        Cancellation Policy
                      </span>
                      <DangerouslyHTMLTextPriview
                        content={selectedProductDetail?.cancellation_policy?.toString() ?? ""}
                        className={
                          "!paragraph14Regular text-neutral-800 [&>p>span]:!paragraph14Regular [&>p>span]:!text-neutral-800"
                        }
                      />
                    </div>
                  </div>
                ) : null}
                {selectedProductDetail?.terms_and_conditions ? (
                  <div
                    className={" mt-4 flex items-center justify-start rounded-lg  bg-[#FCEBEA] p-4"}
                  >
                    <div className={"flex flex-col gap-1"}>
                      <span className={"text15Semibold text-neutral-1000 flex items-center gap-1"}>
                        <span>
                          <LifePreServer />
                        </span>
                        Terms and Conditions
                      </span>
                      <DangerouslyHTMLTextPriview
                        content={selectedProductDetail?.terms_and_conditions?.toString() ?? ""}
                        className={
                          "!paragraph14Regular text-neutral-800 [&>p>span]:!paragraph14Regular [&>p>span]:!text-neutral-800"
                        }
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
          <div
            className={`xl:ml-8 sm:ml-6 md:sticky xl:col-span-1 lg:col-span-2 md:h-full md:overflow-y-auto ${isFullPage ? "top-[85px]" : "top-0"} self-start`}
            id={"booking-container"}
          >
            <div>
              <div className={"w-full"}>
                <div>{handleCalendar()}</div>
                <div className={"w-full mt-4"}>
                  <div className={"w-full"}>
                    {isProductAvailabilityPerDateDataValidating ? (
                      <div className={"text-center my-2"}>
                        <Spinner size={"md"} light className={"text-primary-400 fill-white"} />
                      </div>
                    ) : (
                      <div className={"mb-4 rounded-md shadow"}>
                        {formattedAndValidatedTimeSlots.length > 0 ? (
                          <div>
                            <MonoSelect
                              placeholder={"Select Time"}
                              value={timeSelectionValue}
                              options={formattedAndValidatedTimeSlots}
                              isSearchable={false}
                              onValueChange={(newValue) => {
                                setSelectedTimeSlot(newValue?.value)
                              }}
                              selectedValueColor={"text-neutral-1000"}
                              menuPosition={"fixed"}
                              className={"bg-whiteBlack-100 !rounded-md"}
                              name={"select_time"}
                              prefixIcon={
                                <TimeIcon
                                  className={"text-neutral-600 text16Regular w-4 h-4 mt-0.5"}
                                />
                              }
                              isFromWidget
                            />
                          </div>
                        ) : (
                          <NoSlotsAvailable />
                        )}
                      </div>
                    )}
                  </div>
                  {isProductPricingPerMonthDataisValidating ? (
                    <>
                      <div className={"text-center my-2"}>
                        <Spinner size={"md"} light className={"text-primary-400 fill-white"} />
                      </div>
                    </>
                  ) : !hideQuantityEdit ? (
                    <>
                      {!isProductAvailabilityPerDateDataValidating &&
                        (selectedTimeSlot?.available > 0 || initialValues?.time) && (
                          <div className={"w-full"}>{handleCounter()}</div>
                        )}
                    </>
                  ) : null}
                </div>
                {selectedTimeSlot?.available === 0 && selectedTimeSlot?.reopenable_reason ? (
                  <div className={"mt-3 sm:mx-0 mx-4"}>
                    <div
                      className={
                        "bg-orange-200 border-t border-b border-yellow-400 p-3 flex items-start"
                      }
                    >
                      <div>
                        <PhoneTelephoneIcon className={"text-yellow-600 mt-1.5"} />
                      </div>
                      <div className={"ml-2"}>{selectedTimeSlot?.reopenable_reason}</div>
                    </div>
                  </div>
                ) : null}
                {selectedTimeSlot &&
                !isProductAvailabilityPerDateDataValidating &&
                selectedProductDetail?.minimum_participants_booking_display_message ? (
                  <div className={"my-3 w-full"}>
                    <div
                      className={`${
                        !isMinimumFullfilledForAll
                          ? "bg-red-200 border-red-500"
                          : "bg-green-200 border-green-800"
                      } border-t border-b p-3 paragraph16Regular`}
                    >
                      <div className={"flex items-start gap-2"}>
                        <div>
                          <TwoPeopleIcon className={"w-4 h-4 mt-0.5"} />
                        </div>
                        <div className={"paragraph14Regular"}>
                          {selectedProductDetail?.minimum_participants_booking_display_message}
                        </div>
                      </div>
                      <div className={"flex items-center gap-1 mt-1"}>
                        <span
                          className={` ${
                            !isMinimumFullfilledForAll ? "bg-red-600" : "bg-green-800"
                          } w-2 h-2 border rounded-full ml-1 mr-2`}
                        />
                        <div
                          className={`text14Medium ${
                            !isMinimumFullfilledForAll ? "text-red-600" : "text-green-800"
                          }`}
                        >{`${sumOfUnitCount ?? 0} Guests`}</div>
                        <Tooltip
                          content={`There is total ${
                            totalGuests ?? 0
                          } guest required. Minimum ${tooltipParticipantData} must required. Maximum you can select any number`}
                          trigger={"hover"}
                          className={"text-xs w-64 text-center"}
                        >
                          <InfoIcon className={"w-3.5 h-3.5"} />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ) : null}
                <Button
                  color={"primary"}
                  className={
                    "md:w-full w-full [&>span]:!text16Medium mt-2 [&>span]:!py-[13px] !h-10"
                  }
                  onClick={handleNextStep}
                  size={"sm"}
                  disabled={
                    (initialValues?.time ? !initialValues?.time : !selectedTimeSlot) ||
                    hasNoParticipants ||
                    !isMinimumFullfilledForAllParticipants ||
                    capacityLoading ||
                    isSubmitting ||
                    Boolean(liveCapacityError) ||
                    isLiveCapacityApiLoading
                  }
                >
                  {isLoading || capacityLoading || isSubmitting || isLiveCapacityApiLoading ? (
                    <Spinner size={"sm"} light className={"text-primary-400 fill-white"} />
                  ) : (
                    <>
                      Next
                      <RightArrow className={"ml-2.5 text-whiteBlack-100"} width={20} height={20} />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
export default ProductDetailsPreview
